import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import api from "../api/axiosInstance";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import StyledInput from "./StyledInput";
import { ModernButton } from "./ModernButton";
import ModernCard from "./ModernCard";

const COLORS = ["#00C49F", "#FF8042", "#0088FE", "#FFBB28", "#A569BD"];

export default function DashboardTab({ fromDate, toDate, applyClicked, setFromDate, setToDate, setApplyClicked, activeRange, handleDateFilter }) {
  const [search, setSearch] = useState("");
  const [blockFilter, setBlockFilter] = useState("");
  const [panchayatFilter, setPanchayatFilter] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [panchayats, setPanchayats] = useState([]);
  const [allWasteData, setAllWasteData] = useState([]);
  const [chartData, setChartData] = useState({ pieData: [], barData: [] });
  const [loading, setLoading] = useState(true);
  const [blockFilters, setBlockFilters] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [cardsCollapsed, setCardsCollapsed] = useState(false);
  const roleId = parseInt(localStorage.getItem("roleId"));
  const savedBlockName = localStorage.getItem("blockName");
  const savedPanchayatName = localStorage.getItem("panchayatName");

  const [filteredWaste, setFilteredWaste] = useState([]);

  // ✅ Fetch Block & Panchayat list
  useEffect(() => {
    api
      .get("/Block")
      .then((res) => {
        if (res.data.success && Array.isArray(res.data.data)) {
          setBlocks(res.data.data);
        }
      })
      .catch((err) => console.error("Error fetching Blocks:", err));
  }, []);

  // ✅ Fetch all waste data once
  // ✅ Fetch all waste data once
useEffect(() => {
  const fetchWasteData = async () => {
    try {
      const response = await api.get("/WasteEntry");
      if (response.data.success) {
        setAllWasteData(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching waste data:", error);
    } finally {
      setLoading(false);
    }
  };
  fetchWasteData();
}, []);

// ✅ ADD THIS HERE 👇 (auto-select based on role)
// ✅ Role-based auto-select and visibility logic
useEffect(() => {
  if (blocks.length === 0) return;

  if (roleId === 1 || roleId === 2) {
    // 🔹 Role 1 & 2 → Show all blocks and all panchayats, but don't preselect
    setBlockFilter([]); // keep unchecked
    const allPanchayats = blocks.flatMap((b) => b.panchayats || []);
    setPanchayats(allPanchayats);
    setPanchayatFilter([]); // keep unchecked too
  } 
  else if (roleId === 3 && savedBlockName) {
    // 🔹 Role 3 → Show only saved block
    setBlockFilter([savedBlockName]);

    // Filter panchayats belonging to that block
    const matchedBlock = blocks.find((b) => b.name === savedBlockName);
    if (matchedBlock) {
      const relatedPanchayats = matchedBlock.panchayats || [];
      setPanchayats(relatedPanchayats);
      setPanchayatFilter(relatedPanchayats.map((p) => p.name));
    }
  } 
  else if (roleId === 4 && savedPanchayatName) {
    // 🔹 Role 4 → Show only saved panchayat
    setBlockFilter([]); // Hide blocks completely
    setPanchayats([{ name: savedPanchayatName }]);
    setPanchayatFilter([savedPanchayatName]);
  }
}, [blocks, roleId, savedBlockName, savedPanchayatName]);



// ✅ When block changes → update Panchayats
useEffect(() => {
  if (Array.isArray(blockFilter) && blockFilter.length > 0) {
    const selectedBlocks = blocks.filter((b) => blockFilter.includes(b.name));
    const combinedPanchayats = selectedBlocks.flatMap((b) => b.panchayats || []);
    const uniquePanchayats = Array.from(
      new Map(combinedPanchayats.map((p) => [p.panchayatId, p])).values()
    );
    setPanchayats(uniquePanchayats);
  } else {
    setPanchayats([]);
  }
  setPanchayatFilter([]);
}, [blockFilter, blocks]);



  // ✅ Filter waste data based on block, panchayat, search text, and date range
  // ✅ Filter waste data based on block, panchayat, search text, and date range
useEffect(() => {
  if (!allWasteData.length) {
    console.warn("⚠️ allWasteData is empty — waiting for API data...");
    return;
  }

  console.log("🔄 Running filter effect...");
  console.log("Role:", roleId);
  console.log("Saved Block:", savedBlockName);
  console.log("Saved Panchayat:", savedPanchayatName);
  console.log("BlockFilter:", blockFilter);
  console.log("PanchayatFilter:", panchayatFilter);

  let filtered = allWasteData;

  // 🔹 Apply search filter
  if (search.trim()) {
    const lowerSearch = search.toLowerCase();
    filtered = filtered.filter(
      (item) =>
        item.blockName?.toLowerCase().includes(lowerSearch) ||
        item.panchayatName?.toLowerCase().includes(lowerSearch)
    );
  }

  if (blockFilter && Array.isArray(blockFilter) && blockFilter.length > 0) {
    filtered = filtered.filter((item) => blockFilter.includes(item.blockName));
  }

  // 🔹 Apply block filter
  if (
  blockFilters && blockFilters !== "" && 
  (!blockFilter || blockFilter.length === 0)
) {
  // Only use blockFilters if dropdown is empty
  filtered = filtered.filter((item) => item.blockName === blockFilters);
} else if (blockFilter && Array.isArray(blockFilter) && blockFilter.length > 0) {
  // Otherwise, use dropdown selection
  filtered = filtered.filter((item) => blockFilter.includes(item.blockName));
}

  // 🔹 Apply panchayat filter
  if (panchayatFilter && Array.isArray(panchayatFilter) && panchayatFilter.length > 0) {
    filtered = filtered.filter((item) => panchayatFilter.includes(item.panchayatName));
  }

  // ✅ Panchayat role fallback
  if (roleId === 4 && savedPanchayatName) {
    filtered = filtered.filter(
      (item) =>
        item.panchayatName?.toLowerCase() === savedPanchayatName.toLowerCase()
    );
  }

  // 🔹 Apply date range
  if (fromDate && toDate) {
    filtered = filtered.filter((item) => {
      const entryDate = new Date(item.date);
      const from = new Date(fromDate);
      const to = new Date(toDate);
      from.setHours(0, 0, 0, 0);
      to.setHours(23, 59, 59, 999);
      return entryDate >= from && entryDate <= to;
    });
  }

  console.log("✅ Filtered data count:", filtered.length);

  // 🔹 Aggregate totals for Pie Chart
  const totals = filtered.reduce(
    (acc, cur) => {
      acc.biodegradableKg += cur.biodegradableKg || 0;
      acc.nonDegradableKg += cur.nonDegradableKg || 0;
      acc.recyclableDryKg += cur.recyclableDryKg || 0;
      acc.householdHazardousKg += cur.householdHazardousKg || 0;
      acc.mixedKg += cur.mixedKg || 0;
      return acc;
    },
    {
      biodegradableKg: 0,
      nonDegradableKg: 0,
      recyclableDryKg: 0,
      householdHazardousKg: 0,
      mixedKg: 0,
    }
  );

  const pieData = [
    { name: "Biodegradable", value: totals.biodegradableKg },
    { name: "Non-Degradable", value: totals.nonDegradableKg },
    { name: "Recyclable Dry", value: totals.recyclableDryKg },
    { name: "Household Hazardous", value: totals.householdHazardousKg },
    { name: "Mixed", value: totals.mixedKg },
  ];

  // 🔹 Prepare Bar Chart data grouped by date
  const grouped = {};
  filtered.forEach((item) => {
    const dateObj = new Date(item.date);
    const dateKey = dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
    
    if (!grouped[dateKey]) {
      grouped[dateKey] = {
        date: dateKey,
        sortDate: dateObj, // Keep original date for sorting
        biodegradable: 0,
        nonDegradable: 0,
        recyclableDry: 0,
        householdHazardous: 0,
        mixed: 0,
      };
    }
    grouped[dateKey].biodegradable += item.biodegradableKg;
    grouped[dateKey].nonDegradable += item.nonDegradableKg;
    grouped[dateKey].recyclableDry += item.recyclableDryKg;
    grouped[dateKey].householdHazardous += item.householdHazardousKg;
    grouped[dateKey].mixed += item.mixedKg;
  });

  // ✅ Sort by date in ascending order (1, 2, 3...)
  const barData = Object.values(grouped)
    .sort((a, b) => new Date(a.sortDate) - new Date(b.sortDate))
    .map(({ sortDate, ...rest }) => rest); // Remove sortDate from final data
  setChartData({ pieData, barData });
}, [
  search,
  blockFilter,
  blockFilters,
  panchayatFilter,
  allWasteData,
  fromDate,
  toDate,
  applyClicked,
  roleId,
  savedPanchayatName,
  savedBlockName,
]);


  if (loading) return <div className="p-4">Loading charts...</div>;

  const { pieData, barData } = chartData;

  const WASTE_LABELS = {
  biodegradable: "Bio Degradable",
  nonDegradable: "Non Degradable",
  recyclableDry: "Recyclable Dry",
  householdHazardous: "Household Hazardous",
  mixed: "Mixed",
};


// useEffect(() => {
//   if (!allWasteData.length) return;

//   const filtered = allWasteData.filter((d) => {
//     const date = new Date(d.date);
//     return (
//       date >= new Date(fromDate) &&
//       date <= new Date(toDate)
//     );
//   });

//   setFilteredWaste(filtered);
// }, [applyClicked, fromDate, toDate]);


// ✅ Filter visible blocks based on role
const visibleBlocks =
  roleId === 3 && savedBlockName
    ? blocks.filter((b) => b.name === savedBlockName)
    : blocks;


    // ✅ Determine if the selected block (card click) has data
const selectedBlockData = blockFilters
  ? allWasteData.filter(
      (item) =>
        item.blockName === blockFilters &&
        (!fromDate || !toDate ||
          (new Date(item.date) >= new Date(fromDate) &&
           new Date(item.date) <= new Date(toDate)))
    )
  : [];

// ✅ Compute total weight for that block
const selectedBlockTotal = selectedBlockData.reduce(
  (sum, cur) =>
    sum +
    (cur.biodegradableKg || 0) +
    (cur.nonDegradableKg || 0) +
    (cur.recyclableDryKg || 0) +
    (cur.householdHazardousKg || 0) +
    (cur.mixedKg || 0),
  0
);

// ✅ Fallback for deciding what to show
const showNoData =
  blockFilters && selectedBlockTotal === 0 && chartData.pieData.length === 0;


  return (
    <div className="p-0 space-y-2">
      {/* 🔹 Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-yellow-100 text-gray-900 shadow-md rounded-lg">
        {/* Left Section - Search and Dropdowns */}
        <div className="flex items-center gap-3 flex-1">
          {/* 🔍 Search Input */}
          <div className="flex-shrink-0">
            <StyledInput
              placeholder="Search by Block / Panchayat..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Block Dropdown */}
          {(roleId === 1 || roleId === 2 || roleId === 3) && (
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel shrink={true}>Blocks</InputLabel>
              <Select
                multiple
                displayEmpty
                value={Array.isArray(blockFilter) ? blockFilter : []}
                onChange={(e) => {
                  const value = e.target.value;
                  // Prevent "selectAll" from being added as a normal value
                  if (value.includes("selectAll")) return;
                  setBlockFilter(value);
                }}
                input={<OutlinedInput label="Blocks" />}
                renderValue={(selected) => {
                  if (roleId === 3 && savedBlockName) return savedBlockName;
                  if (selected.length === 0) return "Select Blocks";
                  return `${selected.length} Selected`;
                }}
                MenuProps={{ PaperProps: { style: { maxHeight: 300, width: 180 } } }}
                disabled={roleId === 3}
              >
                {/* ✅ "Select All" option (controls all others) */}
                <MenuItem
                  value="selectAll"
                  onClick={() => {
                    if (blockFilter.length === visibleBlocks.length) {
                      // If all selected → unselect all
                      setBlockFilter([]);
                    } else {
                      // If not all selected → select all
                      setBlockFilter(visibleBlocks.map((b) => b.name));
                    }
                  }}
                >
                  <Checkbox
                    checked={
                      blockFilter.length > 0 &&
                      blockFilter.length === visibleBlocks.length
                    }
                    indeterminate={
                      blockFilter.length > 0 &&
                      blockFilter.length < visibleBlocks.length
                    }
                    sx={{ p: 0.3, transform: "scale(0.9)" }}
                  />
                  <ListItemText
                    primary="Select All"
                    primaryTypographyProps={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  />
                </MenuItem>

                {/* 🔹 Individual block items */}
                {visibleBlocks.map((block) => (
                  <MenuItem
                    key={block.blockId}
                    value={block.name}
                    sx={{ py: 0.3, minHeight: "28px" }}
                  >
                    <Checkbox
                      checked={blockFilter.includes(block.name)}
                      sx={{ p: 0.3, transform: "scale(0.9)" }}
                    />
                    <ListItemText
                      primary={block.name}
                      primaryTypographyProps={{ fontSize: "0.8rem" }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Panchayat Dropdown */}
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel shrink={true}>Panchayats</InputLabel>
            <Select
              multiple
              displayEmpty
              value={
                roleId === 4 && savedPanchayatName
                  ? [savedPanchayatName]
                  : Array.isArray(panchayatFilter)
                  ? panchayatFilter
                  : []
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes("selectAll")) return; // prevent adding selectAll as value
                setPanchayatFilter(value);
              }}
              input={<OutlinedInput label="Panchayats" />}
              renderValue={(selected) => {
                if (roleId === 4 && savedPanchayatName) return savedPanchayatName;
                if (selected.length === 0) return "Select Panchayats";
                return `${selected.length} Selected`;
              }}
              disabled={roleId === 4}
              MenuProps={{ PaperProps: { style: { maxHeight: 300, width: 180 } } }}
            >
              {/* ✅ Only show "Select All" when Panchayats exist */}
              {panchayats.length > 0 && (
                <MenuItem
                  value="selectAll"
                  onClick={() => {
                    if (panchayatFilter.length === panchayats.length) {
                      // all selected → unselect all
                      setPanchayatFilter([]);
                    } else {
                      // not all selected → select all
                      setPanchayatFilter(panchayats.map((p) => p.name));
                    }
                  }}
                >
                  <Checkbox
                    checked={
                      panchayatFilter.length > 0 &&
                      panchayatFilter.length === panchayats.length
                    }
                    indeterminate={
                      panchayatFilter.length > 0 &&
                      panchayatFilter.length < panchayats.length
                    }
                    sx={{ p: 0.3, transform: "scale(0.9)" }}
                  />
                  <ListItemText
                    primary="Select All"
                    primaryTypographyProps={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  />
                </MenuItem>
              )}

              {/* 🔹 Individual Panchayats */}
              {panchayats.length > 0 ? (
                panchayats.map((p, idx) => (
                  <MenuItem key={idx} value={p.name} sx={{ py: 0.3, minHeight: "28px" }}>
                    <Checkbox
                      checked={panchayatFilter.includes(p.name)}
                      sx={{ p: 0.3, transform: "scale(0.9)" }}
                    />
                    <ListItemText
                      primary={p.name}
                      primaryTypographyProps={{ fontSize: "0.8rem" }}
                    />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled sx={{ fontSize: "0.8rem", color: "#888" }}>
                  No Panchayats Available
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </div>

        {/* Right Section - Date Filter */}
        <div className="flex items-center gap-3">
          {/* Quick Filter Buttons */}
          <div className="flex items-center gap-2">
            {["Today", "Week", "Month"].map((range) => (
              <ModernButton
                key={range}
                onClick={() => handleDateFilter(range)}
                isActive={activeRange === range}
                variant={activeRange === range ? "active" : "default"}
              >
                {range}
              </ModernButton>
            ))}
          </div>

          {/* Calendar Date Range Picker */}
          <div className="relative">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="flex items-center gap-2 px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 transition text-sm"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-gray-700">Date Range</span>
            </button>

            {/* Date Range Dropdown */}
            {showDatePicker && (
              <div className="absolute right-0 mt-2 p-4 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] min-w-80">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-700">Select Date Range</h4>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                      <input 
                        type="date" 
                        value={fromDate} 
                        onChange={(e) => setFromDate(e.target.value)}
                        className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400" 
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                      <input 
                        type="date" 
                        value={toDate} 
                        onChange={(e) => setToDate(e.target.value)}
                        className="w-full border border-gray-300 px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400" 
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end pt-2">
                    <ModernButton
                      onClick={() => {
                        setApplyClicked((prev) => prev + 1);
                        setShowDatePicker(false);
                      }}
                      variant="primary"
                      icon={
                        <svg 
                          className="lucide lucide-check" 
                          strokeLinejoin="round" 
                          strokeLinecap="round" 
                          strokeWidth={2} 
                          stroke="currentColor" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          height={16} 
                          width={16} 
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      }
                    >
                      Apply
                    </ModernButton>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 📊 Charts */}
      {/* 📊 Block Cards + Charts (Single Row) */}
{/* 📊 Block Cards + Charts (Single Row Fit) */}
<div className="flex gap-6 mt-6">
  {/* Left Side - Block Cards */}
  {roleId !== 4 && (
    <div className="w-72 flex-shrink-0">
      {/* Cards Container - 5 Cards Layout */}
      <div className="flex flex-col gap-3 p-2">
        {/* Total Card - Always at Top */}
        <div className="mb-2">
          <ModernCard
            title="Total Collection"
            value={`${visibleBlocks.reduce((total, block) => {
              const blockTotal = allWasteData
                .filter((item) => {
                  if (item.blockName !== block.name) return false;
                  if (fromDate && toDate) {
                    const entryDate = new Date(item.date);
                    const from = new Date(fromDate);
                    const to = new Date(toDate);
                    from.setHours(0, 0, 0, 0);
                    to.setHours(23, 59, 59, 999);
                    return entryDate >= from && entryDate <= to;
                  }
                  return true;
                })
                .reduce(
                  (sum, cur) =>
                    sum +
                    (cur.biodegradableKg || 0) +
                    (cur.nonDegradableKg || 0) +
                    (cur.recyclableDryKg || 0) +
                    (cur.householdHazardousKg || 0) +
                    (cur.mixedKg || 0),
                  0
                );
              return total + blockTotal;
            }, 0).toFixed(2)} Kg`}
            icon={
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth={2}
                width={14}
                height={14}
              >
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27,6.96 12,12.01 20.73,6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            }
            fillPercentage={100}
            iconColor="#8B5CF6"
            fillColor="#8B5CF6"
            percentage={`${visibleBlocks.length} Blocks`}
          />
        </div>

        {/* Individual Block Cards */}
        {visibleBlocks.map((block) => {
          const totalWaste = allWasteData
            .filter((item) => {
              if (item.blockName !== block.name) return false;

              // ✅ Filter by selected calendar date range
              if (fromDate && toDate) {
                const entryDate = new Date(item.date);
                const from = new Date(fromDate);
                const to = new Date(toDate);
                from.setHours(0, 0, 0, 0);
                to.setHours(23, 59, 59, 999);
                return entryDate >= from && entryDate <= to;
              }

              return true;
            })
            .reduce(
              (sum, cur) =>
                sum +
                (cur.biodegradableKg || 0) +
                (cur.nonDegradableKg || 0) +
                (cur.recyclableDryKg || 0) +
                (cur.householdHazardousKg || 0) +
                (cur.mixedKg || 0),
              0
            );

          const isSelected = blockFilters === block.name || blockFilter === block.name;
          
          // Calculate percentage based on max waste across all blocks
          const maxWaste = Math.max(...visibleBlocks.map(b => {
            return allWasteData
              .filter(item => item.blockName === b.name)
              .reduce((sum, cur) => sum + (cur.biodegradableKg || 0) + (cur.nonDegradableKg || 0) + (cur.recyclableDryKg || 0) + (cur.householdHazardousKg || 0) + (cur.mixedKg || 0), 0);
          }));
          
          const fillPercentage = maxWaste > 0 ? Math.round((totalWaste / maxWaste) * 100) : 0;

          return (
            <ModernCard
              key={block.blockId}
              title={block.name}
              value={`${totalWaste.toFixed(2)} Kg`}
              icon={
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth={2}
                  width={14}
                  height={14}
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9,22 9,12 15,12 15,22" />
                </svg>
              }
              fillPercentage={fillPercentage}
              iconColor={isSelected ? "#F59E0B" : "#10B981"}
              fillColor={isSelected ? "#F59E0B" : "#10B981"}
              onClick={() => {
                const newBlock = blockFilters === block.name ? "" : block.name;
                setBlockFilters(newBlock);
              }}
              isSelected={isSelected}
            />
          );
        })}
      </div>
    </div>
  )}

  {/* Right Side - Charts (Side by Side) */}
  <div className="flex-1 flex gap-4">
    {/* Pie Chart - Left */}
    <div className="flex-1 bg-white shadow-md rounded-lg p-4 border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">Waste Type Distribution</h3>
      <div className="flex items-center justify-center" style={{ height: '400px' }}>
        {pieData.length === 0 || pieData.every((d) => d.value === 0) ? (
          <p className="text-gray-400 text-sm font-medium text-center">No data available</p>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                outerRadius={120}
                fill="#8884d8"
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
                  if (percent * 100 < 1) return null;
                  const RADIAN = Math.PI / 180;
                  const radius = outerRadius + 15;
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);
                  return (
                    <text
                      x={x}
                      y={y}
                      textAnchor={x > cx ? "start" : "end"}
                      dominantBaseline="central"
                      style={{ fontSize: "11px", fill: "#333", fontWeight: 500 }}
                    >
                      {`${pieData[index].name} (${(percent * 100).toFixed(1)}%)`}
                    </text>
                  );
                }}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v) => (v ? v.toFixed(2) : "0.00")}
                contentStyle={{ fontSize: "11px" }}
              />
              <Legend verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: "11px" }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>

    {/* Bar Chart - Right */}
    <div className="flex-1 bg-white shadow-md rounded-lg p-4 border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">Daily Waste Collection Trends</h3>
      <div className="flex items-center justify-center" style={{ height: '400px' }}>
        {barData.length === 0 ? (
          <p className="text-gray-400 text-sm font-medium text-center">No data available</p>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={barData}>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                stroke="#555"
                axisLine={true}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                stroke="#555"
                axisLine={true}
                tickLine={false}
                label={{
                  value: "Weight (Kg)",
                  angle: -90,
                  position: "insideLeft",
                  fontSize: 11,
                  fill: "#555",
                }}
                domain={[0, (dataMax) => Math.ceil(dataMax * 1.1)]}
              />
              <Tooltip
                formatter={(value, name) => [value.toFixed(2), WASTE_LABELS[name] || name]}
                labelFormatter={(label) => {
                  if (typeof label === "string") {
                    const dateParts = label.split(" ");
                    if (dateParts.length === 2) {
                      const [day, month] = dateParts;
                      const year = new Date().getFullYear();
                      return `${day} ${month} ${year}`;
                    }
                    return label;
                  }
                  try {
                    const dateObj = new Date(label);
                    if (!isNaN(dateObj)) {
                      return dateObj.toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      });
                    }
                  } catch {
                    return String(label);
                  }
                  return String(label);
                }}
                contentStyle={{ fontSize: "10px" }}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                wrapperStyle={{ fontSize: "10px", marginTop: 10 }}
              />

              {/* ✅ Bar Segments */}
              <Bar dataKey="biodegradable" stackId="a" fill="#00C49F" />
              <Bar dataKey="nonDegradable" stackId="a" fill="#FF8042" />
              <Bar dataKey="recyclableDry" stackId="a" fill="#0088FE" />
              <Bar dataKey="householdHazardous" stackId="a" fill="#FFBB28" />
              <Bar dataKey="mixed" stackId="a" fill="#A569BD">
                {/* ✅ LabelList (Total Value on Top) */}
                <LabelList
                  dataKey={(entry) =>
                    (
                      entry.biodegradable +
                      entry.nonDegradable +
                      entry.recyclableDry +
                      entry.householdHazardous +
                      entry.mixed
                    ).toFixed(2)
                  }
                  position="top"
                  style={{ fontSize: 10, fill: "#333" }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  </div>
</div>
    </div>
  );
}
